"use client";

import { useEffect, useState } from "react";
import { Phone, MapPinned } from "lucide-react";
import { getImageUrl } from "@/lib/utils/image";
import Image from "next/image";

// Lazy load Swiper
let Swiper = null;
let SwiperSlide = null;

/**
 * StoreCard component for individual store display.
 * Extracted for better reuse and cleaner logic.
 */
function StoreCard({ store }) {
  const imageUrl = getImageUrl(store.image);

  return (
    <div className="glass-box rounded-36 w-[320px] md:w-[500px] h-auto md:h-[220px] flex flex-col md:flex-row p-4 md:p-5 gap-5 transition-all hover:border-white/20">
      <div className="w-full md:w-[200px] h-[160px] md:h-full shrink-0 relative overflow-hidden rounded-2xl">
        <Image
          src={imageUrl || "/image/hero.png"}
          fill
          alt={store.title}
          className="object-cover"
          sizes="(max-width: 768px) 320px, 200px"
        />
      </div>

      <div className="flex flex-col justify-between py-1 flex-1">
        <div className="space-y-2">
          <h3 className="text-white text-lg font-dana-medium leading-tight">
            {store.title}
          </h3>
          <p className="text-mute text-xs md:text-sm line-clamp-2 leading-relaxed font-dana-light">
            {store.province} - {store.address}
          </p>
        </div>

        <div className="space-y-3 mt-4">
          <a
            href={`tel:${store.phone}`}
            className="flex gap-x-3 items-center text-mute text-sm hover:text-white transition-colors group"
          >
            <Phone size={18} className="text-white stroke-[1.5px] group-hover:scale-110 transition-transform" />
            <span dir="ltr" className="font-dana-regular">{store.phone}</span>
          </a>

          {store.latitude && store.longitude && (
            <a
              href={`https://www.google.com/maps?q=${store.latitude},${store.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex gap-x-3 items-center text-mute text-sm hover:text-white transition-colors group"
            >
              <MapPinned size={18} className="text-white stroke-[1.5px] group-hover:scale-110 transition-transform" />
              <span className="font-dana-regular">مشاهده روی نقشه</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * SelectedStores component displays a carousel of store cards.
 */
export default function SelectedStores({ stores = [] }) {
  const [swiperLoaded, setSwiperLoaded] = useState(false);

  useEffect(() => {
    // Lazy load Swiper
    Promise.all([
      import("swiper/react"),
      import("swiper/css")
    ]).then(([swiperModule]) => {
      Swiper = swiperModule.Swiper;
      SwiperSlide = swiperModule.SwiperSlide;
      setSwiperLoaded(true);
    }).catch(console.error);
  }, []);

  if (!stores?.length) return null;

  // Fallback before Swiper loads
  if (!swiperLoaded || !Swiper) {
    return (
      <div className="w-full overflow-hidden">
        <div className="flex gap-5 overflow-x-auto px-4 md:px-0 pb-4 scrollbar-hide">
          {stores.slice(0, 3).map((store) => (
            <div key={store.id} className="flex-shrink-0">
              <StoreCard store={store} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <Swiper
        slidesPerView="auto"
        spaceBetween={20}
        className="w-full overflow-hidden px-4 md:overflow-visible md:px-0"
        grabCursor
      >
        {stores.map((store) => (
          <SwiperSlide key={store.id} className="!w-auto">
            <StoreCard store={store} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

